import { cloneDeep, forEach } from 'lodash'

import Vue from '@/setup'
import store from '@/store'
import { EVENT_COLLECTIVE_LOADED } from '@/config/events'
import { DistributionHelper } from '@/helpers/Distribution'
import { CollectiveUtil } from '@/utils/Collective'
import { CHANNEL_PARTNERWIDGET, CHANNEL_SERVICEPORTAL, COLLECTIVE_REQUIRED_FLAGS } from '@/config/constants'

const { $EventBus, $backend } = Vue.prototype

export const CollectiveHelper = {
  ...CollectiveUtil,
  /**
   * @param {string} contractNumber
   * @param {Object} basketSettings
   * @returns {CollectiveSettings || undefined}
   */
  getCollectiveSettings(contractNumber, basketSettings = {}) {
    if (!basketSettings.channel) {
      throw new Error(`Invalid parameter basketChannel: ${basketSettings.channel}`)
    }
    if (!basketSettings.distributionPartners) {
      throw new Error(`Invalid parameter basketDistributionPartners: ${basketSettings.distributionPartners}`)
    }

    const contract = store.getters['content/getCollective'](contractNumber)
    if (!contract) return

    const storedCollective = cloneDeep(store.state.basket?.collective)
    const { channel, distributionPartnerSanitas, masterTreaty } = contract
    const distributionPartners = [CHANNEL_PARTNERWIDGET, CHANNEL_SERVICEPORTAL].includes(basketSettings.channel)
      ? basketSettings.distributionPartners
      : DistributionHelper.createDistributionPartners(distributionPartnerSanitas)

    return {
      collective: {
        channel,
        contractNumber,
        personnelNumber: storedCollective?.personnelNumber || basketSettings.personnelNumber,
        startDate: storedCollective?.startDate || basketSettings.startDate,
      },
      distributionPartners,
      masterTreaty,
    }
  },

  /**
   * Loads the collective contract structure from current basket
   *
   * @param {BasketPayload} payload
   * @returns {Promise<Basket | BasketPayload>}
   * @private
   */
  loadCollective(payload) {
    const contractStartDate = payload.contractStartDate
    const contractNumber = payload?.collective?.contractNumber
    if (!contractNumber || !contractStartDate) {
      return Promise.resolve(payload)
    }

    return $backend.ovp
      .getCollective({ contractNumber, contractStartDate })
      .then(response => {
        $EventBus.$emit(EVENT_COLLECTIVE_LOADED, response)
        return this.loadCollectiveContent(payload, response)
      })
      .then(response => store.dispatch('content/setCollective', response))
      .then(() => updateBasketForCollective(contractNumber, payload))
      .then(basket => store.dispatch('basket/setBasket', basket))
  },

  /**
   * Loads the collective content when contractNumber given
   *
   * @param {BasketPayload} payload
   * @param {CollectiveResponse} config
   * @returns {Promise<CollectiveInfo | void>}
   * @private
   */
  loadCollectiveContent(payload, config) {
    const { channel, contractNumber } = config
    if (!contractNumber || !channel) {
      return Promise.resolve()
    }

    let { language } = payload
    language = language.toLowerCase()

    const content = require(`@/language/collective/collective.${language}.json`)
    return Promise.resolve(Object.assign({}, content[channel], content[contractNumber], config))
  },

  /**
   * @param {ConditionFile[]} conditions
   * @param {Person[]} persons
   * @param {string} contractNumber
   * @returns {ConditionFile[]}
   */
  getFilteredConditions(conditions, persons, contractNumber) {
    if (!conditions) {
      return []
    }
    const products = getConditionProducts(persons, contractNumber)
    return conditions.filter(file => CollectiveUtil.filterFileByProducts(file, products))
  },

  isPersonnelNumberRequired() {
    const state = store.getters['content/getCollectiveGroup']()
    return COLLECTIVE_REQUIRED_FLAGS.includes(state?.personnelNumberDescriptor?.state)
  },
}

/**
 * @param {Person[]} persons
 * @param {string} contractNumber
 * @returns {ConditionProduct[]}
 */
function getConditionProducts(persons, contractNumber) {
  const products = []

  persons.forEach(person => {
    let collective
    if (person.collectivePersonGroup) {
      collective = {
        contractNumber,
        personGroup: person.collectivePersonGroup,
      }
    }

    const selectedProducts = store.getters['basket/getSelectedProductsOfPerson'](person.personId)
    forEach(selectedProducts, product => {
      products.push({
        category: store.getters['products/getCategoryIdFromProduct'](product.id),
        group: store.getters['products/getGroupIdFromProduct'](product.id),
        product: product.id,
        collective,
      })
    })
  })
  return products
}

/**
 * @param {string} contractNumber
 * @param {BasketPayload} payload
 * @returns {Promise<Basket | BasketPayload>}
 */
function updateBasketForCollective(contractNumber, payload) {
  const basket = cloneDeep(payload)
  const basketSettings = {
    channel: basket.channel,
    distributionPartners: basket.distributionPartners,
    personnelNumber: basket.collective?.personnelNumber,
    startDate: basket.collective?.startDate,
  }
  const settings = CollectiveHelper.getCollectiveSettings(contractNumber, basketSettings)
  return Promise.resolve(Object.assign(basket, settings))
}
